import { useState, useEffect, useRef } from "react";
import { EMPTY_CATS, loadTD, savePriority, loadPlanResult, savePlanResult, clearPlanResult, loadPlanForm, savePlanForm, clearPlanForm } from "../../lib/storage";

const MILESTONE_COLORS = ["#ec4899","#0ea5e9","#10b981"];

const PATH_META = {
  conservative: { label:"Conservative", emoji:"🛡️", color:"#8b5cf6", desc:"Protect capital with steady, predictable growth" },
  balanced:     { label:"Balanced",     emoji:"⚖️", color:"#0ea5e9", desc:"Balanced growth with moderate risk" },
  aggressive:   { label:"Aggressive",   emoji:"🚀", color:"#f59e0b", desc:"Maximize long-term growth, embrace volatility" },
};

const GROWTH_RATES = { conservative: 0.055/12, balanced: 0.075/12, aggressive: 0.095/12 };

function getAllocForRisk(risk, age) {
  if (risk === "conservative") {
    const stocks = Math.max(30, Math.min(50, 65 - age));
    const bonds  = Math.max(30, Math.min(55, age + 20));
    const reit   = Math.max(5, 100 - stocks - bonds);
    return { stocks, bonds, reit, alternatives: 0 };
  }
  if (risk === "aggressive") {
    const stocks       = Math.max(75, Math.min(88, 110 - age));
    const reit         = 7;
    const alternatives = 5;
    const bonds        = Math.max(0, 100 - stocks - reit - alternatives);
    return { stocks, bonds, reit, alternatives };
  }
  // balanced
  const stocks = Math.max(55, Math.min(80, 100 - age));
  const bonds  = Math.max(10, Math.min(25, age - 5));
  const reit   = Math.max(5, 100 - stocks - bonds);
  return { stocks, bonds, reit, alternatives: 0 };
}

function RoadmapSection({ roadmap, accentColor }) {
  return (
    <div style={{ background:"#1e293b", borderRadius:18, padding:"16px", border:"1px solid #334155" }}>
      <div style={{ fontSize:11, fontWeight:900, color: accentColor || "#ec4899", textTransform:"uppercase", letterSpacing:1.5, marginBottom:12 }}>🗺️ Your Roadmap</div>
      <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
        {roadmap.map((m, i) => {
          const color = MILESTONE_COLORS[i] || "#10b981";
          const hasSteps = m.steps && m.steps.length > 0;
          return (
            <div key={i} style={{ display:"flex", gap:12, alignItems:"stretch" }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", width:24, flexShrink:0 }}>
                <div style={{ width:24, height:24, borderRadius:"50%", background:color,
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:900, color:"#fff", flexShrink:0 }}>
                  {i+1}
                </div>
                {i < roadmap.length-1 && <div style={{ width:2, flex:1, background:"#334155", margin:"4px 0" }}/>}
              </div>
              <div style={{ paddingBottom: i < roadmap.length-1 ? 20 : 0, flex:1, minWidth:0 }}>
                <div style={{ fontSize:10, color:"#64748b", fontWeight:800, textTransform:"uppercase", letterSpacing:1 }}>{m.t}</div>
                <div style={{ fontSize:14, fontWeight:900, color:"#f1f5f9", marginTop:2 }}>{m.label}</div>
                <div style={{ fontSize:13, fontWeight:900, color, marginTop:2 }}>{m.v}</div>
                <div style={{ fontSize:12, color:"#64748b", fontWeight:700, lineHeight:1.5, marginTop:4 }}>{m.desc}</div>
                {hasSteps && (
                  <div style={{ marginTop:12, background:"#0f172a", borderRadius:12, padding:"12px 14px", border:`1px solid ${color}44` }}>
                    <div style={{ fontSize:10, fontWeight:900, color, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>How to get there</div>
                    <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                      {m.steps.map((s, j) => (
                        <div key={j} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                          <div style={{ width:20, height:20, borderRadius:"50%", background:color,
                            flexShrink:0, marginTop:1, display:"flex", alignItems:"center",
                            justifyContent:"center", fontSize:10, fontWeight:900, color:"#fff" }}>
                            {j+1}
                          </div>
                          <div style={{ fontSize:12, color:"#cbd5e1", fontWeight:700, lineHeight:1.6, flex:1 }}>{s}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProPlanScreen({ trackerData, persistTracker, onResetPro }) {
  const persistTrackerRef = useRef(persistTracker);
  useEffect(() => { persistTrackerRef.current = persistTracker; }, [persistTracker]);

  const STEPS = [
    { id:"basics", label:"The Basics", emoji:"👤" },
    { id:"money",  label:"Your Money", emoji:"💰" },
    { id:"debt",   label:"Your Debt",  emoji:"💳" },
    { id:"goals",  label:"Your Goals", emoji:"🎯" },
  ];

  const [step, setStep]         = useState(0);
  const [form, setForm]         = useState(() => loadPlanForm() || {
    age:"", income:"", savings:"", investments:"", debt:"", debtRate:"",
    monthlyExpenses:"", riskTolerance:"medium", goals:[], timeline:"", customGoal:""
  });
  const [plan, setPlan]         = useState(() => loadPlanResult());
  const [activeTab, setActiveTab] = useState(() => loadPlanResult()?.defaultTab || "balanced");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  const GOAL_OPTIONS = ["Buy a home","Pay off debt","Retire early","Build emergency fund","Start investing","Save for college","Build side income","Travel fund","Start a business","Grow wealth"];

  function set(k, v) { setForm(f => ({ ...f, [k]:v })); }
  function toggleGoal(g) {
    setForm(f => ({ ...f, goals: f.goals.includes(g) ? f.goals.filter(x => x !== g) : [...f.goals, g] }));
  }

  function handleReset() {
    if (!window.confirm("Reset all Pro data? This clears your plan, tracker, budget, and priority settings.")) return;
    onResetPro();
    clearPlanResult();
    clearPlanForm();
    setForm({ age:"", income:"", savings:"", investments:"", debt:"", debtRate:"",
      monthlyExpenses:"", riskTolerance:"medium", goals:[], timeline:"", customGoal:"" });
    setPlan(null);
    setStep(0);
  }

  function canAdvance() {
    if (step === 0) return form.age && form.income && form.monthlyExpenses;
    if (step === 1) return true;
    if (step === 2) return true;
    if (step === 3) return form.goals.length > 0 || form.customGoal;
    return true;
  }

  async function generatePlan() {
    setLoading(true); setError(null); setPlan(null);

    const today        = new Date().toISOString().slice(0, 10);
    const income       = parseFloat(form.income)       || 0;
    const monthlyIncome = income / 12;
    const expenses     = parseFloat(form.monthlyExpenses) || 0;
    const surplus      = monthlyIncome - expenses;
    const savingsRate  = monthlyIncome > 0 ? Math.round((surplus / monthlyIncome) * 100) : 0;
    const savings      = parseFloat(form.savings)      || 0;
    const investments  = parseFloat(form.investments)  || 0;
    const debt         = parseFloat(form.debt)         || 0;
    const debtRate     = parseFloat(form.debtRate)     || 0;
    const age          = parseInt(form.age)            || 25;
    const goals        = [...form.goals, ...(form.customGoal ? [form.customGoal] : [])];
    const netWorth     = savings + investments - debt;
    const emergTarget  = expenses * 6;
    const hasFund      = savings >= emergTarget && emergTarget > 0;
    const highRateDebt  = debt > 0 && debtRate > 7;
    const creditCardDebt = debt > 0 && debtRate > 15;
    const currentAssets = savings + investments;
    const fiNumber      = expenses * 12 * 25;

    // ── Situation-based priority (same across all paths) ──
    function getPriority() {
      if (creditCardDebt) return {
        title: "Destroy Your High-Interest Debt",
        body: `You're paying roughly $${(debt*debtRate/100/12).toFixed(0)}/month in interest on $${debt.toLocaleString()} at ${debtRate}%. That's a ${debtRate}% guaranteed return just from paying it off — no investment reliably beats that. Put every extra dollar toward the debt. If you have $${savings.toLocaleString()} in savings beyond a $1,000 emergency buffer, consider using $${Math.max(0, savings-1000).toLocaleString()} of it to immediately reduce the balance.`
      };
      if (!hasFund && emergTarget > 0) return {
        title: "Build Your Emergency Fund First",
        body: `You need $${(emergTarget-savings).toLocaleString()} more for a fully-funded emergency fund (${expenses > 0 ? "6 months × $"+expenses.toLocaleString() : "6 months of expenses"}). Without this cushion any unexpected expense forces debt. Open a high-yield savings account (4%+ APY) and auto-transfer $${Math.min(surplus*0.5, emergTarget-savings).toFixed(0)}/month until funded.`
      };
      if (income > 0 && investments < 1000) return {
        title: "Start Investing — Even a Little",
        body: `You have a $${surplus.toFixed(0)}/month surplus and currently $${investments.toLocaleString()} invested. Open a Roth IRA and put it in a simple index fund. Set it to auto-invest monthly. At your age, starting now vs waiting 5 years could mean over $${Math.round(Math.min(surplus*0.4, 500)*12*5*0.5/1000)*1000}+ in additional wealth at retirement.`
      };
      if (savings > 0 && investments === 0 && !hasFund) return {
        title: "Split Your Cash: Emergency Fund + Investments",
        body: `You have $${savings.toLocaleString()} in cash but nothing invested. Keep $${emergTarget.toFixed(0)} as your emergency fund in a HYSA. Move the remaining $${Math.max(0, savings-emergTarget).toFixed(0)} into a Roth IRA invested in index funds. Then direct $${(surplus*0.3).toFixed(0)}/month toward investments going forward.`
      };
      return {
        title: `Increase Your Savings Rate from ${savingsRate}% to ${Math.min(savingsRate+10, 50)}%`,
        body: `Your current ${savingsRate}% savings rate gives you a $${surplus.toFixed(0)}/month surplus. Raising it by 10 points means investing an extra $${(monthlyIncome*0.1).toFixed(0)}/month — that's $${(monthlyIncome*0.1*12).toFixed(0)}/year. Over 20 years at 8%, that extra amount alone grows to $${Math.round(monthlyIncome*0.1*12*45.76/1000)*1000}+.`
      };
    }
    const priority = getPriority();

    function getCatPriority() {
      if (creditCardDebt) return ["creditCard","student","carLoan","other","emergency","savings","invest"];
      if (!hasFund && emergTarget > 0) return ["emergency","savings","creditCard","student","carLoan","other","invest"];
      if (investments < 1000) return ["invest","savings","emergency","creditCard","student","carLoan","other"];
      if (savings > 0 && investments === 0 && !hasFund) return ["emergency","invest","savings","creditCard","student","carLoan","other"];
      return ["savings","invest","emergency","creditCard","student","carLoan","other"];
    }
    const catPriority = getCatPriority();

    function getStop() {
      if (creditCardDebt) return { t:"Carrying a Credit Card Balance Month to Month", d:`You're donating $${(debt*debtRate/100/12).toFixed(0)}/month to your credit card company in pure interest. Pay the full statement balance every month — or if you can't, stop using the card entirely until it's clear.` };
      if (savingsRate < 10) return { t:"Saving What's Left Over at the End of the Month", d:"If you wait until the end of the month to save, you'll usually find there's nothing left. Savings need to happen automatically on payday, before you can spend. Flip the order." };
      if (investments === 0 && surplus > 0) return { t:"Keeping All Your Money in Cash", d:`Your $${savings.toLocaleString()} in savings is losing purchasing power to inflation every year. Cash is for emergencies only — anything beyond 6 months of expenses should be invested.` };
      if (savingsRate > 30 && debt > 0 && debtRate > 5) return { t:`Over-Investing While Carrying ${debtRate}% Debt`, d:`With ${debtRate}% interest debt, every dollar extra you invest might earn 8-10% while the debt costs ${debtRate}%. The math says pay down the debt first.` };
      return { t:"Lifestyle Inflation with Every Raise", d:"As income grows, expenses tend to grow just as fast — leaving savings rate unchanged. Commit to banking at least half of every raise or bonus before adjusting your lifestyle." };
    }
    const stop = getStop();

    // ── Per-path builders ──
    function buildPath(risk) {
      const alloc = getAllocForRisk(risk, age);
      const mr    = GROWTH_RATES[risk];

      // FI projection at this path's growth rate
      let fiMonths = 0;
      if (surplus > 0) {
        let bal = currentAssets;
        while (bal < fiNumber && fiMonths < 600) { bal = bal*(1+mr) + surplus; fiMonths++; }
      }
      const pathFiYears = Math.round(fiMonths / 12);
      const pathFiAge   = age + pathFiYears;

      // ── Vehicles ──
      function getVehicles() {
        if (risk === "conservative") return [
          { a:"Step 1", d:"Capture full employer 401k match — guaranteed 50-100% return regardless of market conditions, always first" },
          { a:"Step 2", d:"Open a high-yield savings account (Ally, Marcus, SoFi — 4%+ APY) for emergency fund + short-term savings" },
          { a:"Step 3", d:"Open a Roth IRA on Fidelity or Vanguard — invest in a target-date fund (e.g. VTIVX) or 3-fund portfolio: VTI + BND + VXUS" },
          { a:"Step 4", d:"Buy up to $10,000/yr in I-Bonds at TreasuryDirect.gov — inflation-protected, government-backed, beats most savings accounts" },
        ];
        if (risk === "aggressive") return [
          { a:"Step 1", d:"Capture full 401k employer match, then max your Roth IRA ($7,000/yr) — tax-free growth for your most aggressive positions" },
          { a:"Step 2", d:"Open a taxable brokerage (Fidelity or Schwab) — no annual cap, invest beyond retirement account limits" },
          { a:"Step 3", d:"Auto-DCA into growth ETFs: QQQ (Nasdaq 100), SCHG (US Large Growth), or VGT (tech sector) — high beta, long-term hold" },
          { a:"Step 4", d:"Allocate 5-10% of portfolio to Bitcoin or Ethereum (Coinbase or Fidelity Crypto) — asymmetric upside, expect high volatility" },
        ];
        return [
          { a:"Step 1", d:"Capture full employer 401k match first — it's a guaranteed 50-100% return on those dollars" },
          { a:"Step 2", d:"Max your Roth IRA ($7,000/yr) — invest in a low-cost S&P 500 index fund (VTI or FSKAX)" },
          { a:"Step 3", d:"Max 401k to IRS limit ($23,000/yr) before opening a taxable brokerage account" },
        ];
      }

      // ── 30-day actions ──
      function getActions() {
        const acts = [];
        if (risk === "conservative") {
          if (creditCardDebt) acts.push(`Call your credit card company and request a lower rate — or transfer to a 0% balance card to buy payoff time`);
          if (!hasFund) acts.push(`Open a HYSA (Ally, Marcus, SoFi — 4%+ APY) labeled "Emergency Fund" and auto-transfer $${Math.min(Math.round(surplus*0.5), Math.max(0, Math.round(emergTarget-savings))).toLocaleString()}/month`);
          acts.push(`Log into your employer's 401k portal and confirm you're capturing the full match — free guaranteed return, do this first`);
          acts.push(`Open a Roth IRA on Fidelity or Vanguard and invest in a target-date fund (VTIVX or FFFDX) — automatic diversification and rebalancing`);
          acts.push(`Buy $1,000 in I-Bonds at TreasuryDirect.gov — inflation-protected, government-backed, currently outperforming many savings accounts`);
          acts.push(`Ladder short-term CDs: split idle cash into 3-month, 6-month, and 1-year CDs for guaranteed, staggered returns`);
        } else if (risk === "aggressive") {
          if (creditCardDebt) acts.push(`Pay off your ${debtRate}% credit card debt aggressively — this is a guaranteed return no growth stock can reliably beat`);
          if (!hasFund) acts.push(`Build a lean 3-month emergency buffer ($${Math.round(expenses*3).toLocaleString()}) in a HYSA — minimum safety net, then deploy capital to growth`);
          acts.push(`Capture your full 401k match, then open a Roth IRA — tax-free growth is the most powerful lever for long-term aggressive investing`);
          acts.push(`Open a taxable brokerage on Fidelity or Schwab and set up auto-DCA of $${Math.round(surplus*0.4).toLocaleString()}/month into QQQ or SCHG`);
          acts.push(`Allocate 5-10% of investment portfolio to Bitcoin or Ethereum — buy and hold only, don't trade short-term volatility`);
          acts.push(`Pick 2-3 individual stocks in industries you know well — limit to 15% of total portfolio, plan to hold 5+ years`);
        } else {
          if (creditCardDebt) acts.push(`Call your highest-rate credit card (${debtRate}% APR) and negotiate a lower rate — or look into a 0% balance transfer`);
          if (!hasFund) acts.push(`Open a HYSA (SoFi, Marcus, Ally — 4%+ APY) and transfer $${Math.min(Math.round(surplus*0.5), Math.max(0, Math.round(emergTarget-savings))).toFixed(0)} to start your emergency fund`);
          if (investments < 1000) acts.push(`Open a Roth IRA on Fidelity or Vanguard (free, 15 min) and make your first deposit of $${Math.min(100, Math.round(surplus*0.3))}`);
          if (!form.goals.includes("Build emergency fund") && hasFund && investments < 6000) acts.push(`Max out your Roth IRA — set up $${Math.min(583, Math.round(surplus*0.4))}/month automatic contributions`);
          if (income > 0) acts.push(`Log into your employer's benefits portal and confirm you're getting the full 401k match — capture every dollar of free money`);
          acts.push(`Set up automatic transfers: $${Math.round(surplus*0.5)}/month to savings/investments on payday — pay yourself first`);
          acts.push(`Track every purchase for 7 days in your Notes app — most people find 1-2 surprising spending leaks`);
        }
        return acts.slice(0, 5);
      }

      // ── Roadmap ──
      function getRoadmap() {
        const milestones = [];

        function fmtMonths(m) {
          if (m <= 0) return "Now";
          if (m <= 2) return "1-2 months";
          if (m <= 12) return `${Math.ceil(m)} months`;
          const yrs = Math.round(m / 12);
          return yrs <= 1 ? "~1 year" : `~${yrs} years`;
        }

        // Milestone 1 — Emergency fund
        const efTarget = risk === "aggressive" ? expenses * 3 : emergTarget || expenses * 6;
        if (!hasFund || (risk === "aggressive" && savings < expenses * 3)) {
          const gap = Math.max(0, efTarget - savings);
          const monthlyToward = Math.max(surplus * (risk === "aggressive" ? 0.4 : 0.5), 50);
          const months = Math.ceil(gap / monthlyToward);
          const stepsMap = {
            conservative: [
              `Open a HYSA (Ally, Marcus, SoFi — 4%+ APY) labeled "Emergency Fund" and auto-transfer $${Math.max(Math.round(monthlyToward), 50).toLocaleString()}/month on payday`,
              `Target 6 full months of expenses ($${Math.round(efTarget).toLocaleString()}) — conservative strategy means a full cushion before investing aggressively`,
              savings > 0 ? `Move $${Math.min(savings, gap).toLocaleString()} of your current savings to the HYSA today for the higher APY while building toward the target` : `Start with even $50/month if needed — the habit matters more than the amount at the start`,
              `Once funded, never touch it for non-emergencies — this is your insurance policy, not your savings account`,
            ].filter(Boolean),
            balanced: [
              `Open a HYSA (Ally, SoFi, Marcus — 4%+ APY) and auto-transfer $${Math.max(Math.round(monthlyToward), 50).toLocaleString()}/month`,
              savings > 0 ? `Move $${Math.min(savings, gap).toLocaleString()} of your current savings to the HYSA today to jumpstart progress` : `Build the habit: start with even $50/month — momentum matters more than the amount`,
              creditCardDebt ? `Pause extra debt payments temporarily — a small emergency fund prevents you from adding more debt when life happens` : `Don't touch this account for anything except a true emergency (job loss, medical, car failure)`,
            ].filter(Boolean),
            aggressive: [
              `Keep a lean 3-month buffer ($${Math.round(efTarget).toLocaleString()}) in HYSA — enough protection without hoarding cash that should be invested`,
              `Auto-transfer $${Math.max(Math.round(monthlyToward), 50).toLocaleString()}/month — get this done fast so you can redirect capital to growth positions`,
              `Once the 3-month buffer is complete, redirect every surplus dollar toward investing — idle cash loses to inflation`,
            ],
          };
          milestones.push({
            t: fmtMonths(months),
            label: "Emergency Fund Complete",
            v: "$" + Math.round(efTarget).toLocaleString(),
            desc: risk === "aggressive"
              ? "Lean 3-month buffer locked in — protect your downside, then deploy capital to growth."
              : risk === "conservative"
              ? "Full 6-month safety net funded before you invest aggressively."
              : "Financial safety net locked in. No bad surprise can knock you off track.",
            steps: stepsMap[risk],
          });
        } else {
          const stepsMap = {
            conservative: [
              `Keep this in a HYSA earning 4%+ APY — your emergency fund should work while it waits`,
              `Review every 6 months to confirm it still covers 6 full months of expenses as costs change`,
              `Redirect surplus: everything beyond this cushion goes to CDs, I-bonds, or your Roth IRA`,
            ],
            balanced: [
              `Move these funds to a HYSA if you haven't (4%+ APY) — your emergency fund should be earning money`,
              `Set a calendar reminder every 6 months to confirm it still covers 6 months of expenses`,
              `Every extra dollar beyond this cushion now goes toward investing`,
            ],
            aggressive: [
              `Your buffer is funded — now it's time to deploy capital aggressively into growth assets`,
              `Keep exactly 3 months in HYSA ($${Math.round(expenses*3).toLocaleString()}) — anything beyond that should be invested, not sitting idle`,
              `Move excess cash above the 3-month buffer into your brokerage account immediately`,
            ],
          };
          milestones.push({
            t: "Now ✅",
            label: "Emergency Fund Complete",
            v: "$" + savings.toLocaleString(),
            desc: "Already done — this is your foundation.",
            steps: stepsMap[risk],
          });
        }

        // Milestone 2 — Investment momentum
        const target2 = currentAssets < 5000 ? 10000 : Math.max(currentAssets*3, 25000);
        const gap2 = Math.max(0, target2 - currentAssets);
        const monthlyInvest = Math.max(surplus * 0.6, 50);
        const months2 = gap2 > 0 ? Math.ceil(gap2 / monthlyInvest) : 0;
        const yr2label = months2 <= 0 ? "Now" : fmtMonths(Math.min(months2, 120));
        const stepsM2 = {
          conservative: [
            income > 0 ? `Capture your full 401k employer match first — guaranteed return that no market instrument can beat` : `Open a Roth IRA on Fidelity or Vanguard and invest in a target-date fund — automatic rebalancing built in`,
            `Invest in a total bond market fund (BND or VBTLX) for the bond portion — low cost, diversified, low volatility`,
            `Build a simple 3-fund portfolio: VTI (US stocks) + BND (bonds) + VXUS (international) in your target allocation (${alloc.stocks}/${alloc.bonds}/${alloc.reit})`,
            `Look into I-Bonds at TreasuryDirect.gov ($10k/yr max) — inflation-protected and a safe complement`,
          ],
          balanced: [
            income > 0 ? `Capture full 401k match first — free money with a guaranteed return` : `Open a Roth IRA on Fidelity or Vanguard (free, 15 min) — your first investment account`,
            investments < 7000 ? `Contribute $${Math.min(Math.round(monthlyInvest), 583).toLocaleString()}/month to Roth IRA — invest in VTI or FSKAX (low-cost S&P 500 index fund)` : `Increase your automatic monthly investment by $${Math.max(Math.round(monthlyInvest*0.2), 50)} — small increases compound dramatically`,
            `Index funds only — set it and forget it beats active trading for 90%+ of people over 10+ year horizons`,
            `Every raise: immediately increase investment contribution by half the raise before lifestyle inflation takes it`,
          ],
          aggressive: [
            income > 0 ? `Capture full 401k match, then max Roth IRA — tax-free growth is where you want your best growth positions` : `Open a Roth IRA and taxable brokerage immediately — the brokerage has no annual contribution cap`,
            `Open a taxable brokerage on Fidelity or Schwab and set up auto-DCA of $${Math.round(monthlyInvest).toLocaleString()}/month into QQQ, SCHG, or VGT`,
            `Allocate 5-10% to crypto (Bitcoin or Ethereum only) — buy on Coinbase or Fidelity Crypto, hold long-term, ignore volatility`,
            `Research individual stocks in sectors you know — limit to 10-15% of total portfolio, plan 5+ year holds`,
          ],
        };
        milestones.push({
          t: yr2label,
          label: "Investment Momentum",
          v: "$" + target2.toLocaleString(),
          desc: risk === "aggressive"
            ? "Portfolio gaining momentum — compounding accelerates your growth-focused positions."
            : risk === "conservative"
            ? "Portfolio growing steadily — compounding builds a reliable, low-volatility base."
            : "Compounding starts working for you — your money begins generating real returns on its own.",
          steps: stepsM2[risk],
        });

        // Milestone 3 — FI or 5-year
        if (surplus > 0 && fiMonths > 0 && fiMonths <= 600) {
          const extraMonthly = Math.round(monthlyIncome * 0.05);
          const yearsSaved = pathFiYears > 5 ? Math.round(pathFiYears * 0.15) : 1;
          const stepsM3 = {
            conservative: [
              `FI target: $${fiNumber.toLocaleString()} — track portfolio progress quarterly, expect steady and predictable growth at ~${(GROWTH_RATES.conservative*12*100).toFixed(1)}%/yr`,
              `In the 5 years before FI, shift to even more conservative allocation (60%+ bonds) to protect what you've built`,
              `Consider dividend ETFs (VYM, SCHD) as you approach FI — income generation over capital appreciation`,
              `Boosting monthly savings by $${extraMonthly.toLocaleString()} could shave ~${yearsSaved} year${yearsSaved !== 1 ? "s" : ""} off your timeline`,
            ],
            balanced: [
              `FI number: $${fiNumber.toLocaleString()} — 25× annual expenses. Track quarterly to watch yourself close the gap`,
              `Boost monthly savings by $${extraMonthly.toLocaleString()} to shave ~${yearsSaved} year${yearsSaved !== 1 ? "s" : ""} off timeline — find one subscription to cut`,
              `Increase savings rate with every raise: commit to saving at least half of every pay increase`,
              `Rebalance annually to target allocation (${alloc.stocks}% stocks / ${alloc.bonds}% bonds / ${alloc.reit}% REITs)`,
            ],
            aggressive: [
              `FI target: $${fiNumber.toLocaleString()} — your aggressive growth rate (~${(GROWTH_RATES.aggressive*12*100).toFixed(1)}%/yr) projects FI at age ${pathFiAge}`,
              `Every raise goes straight to investments — commit to banking 70%+ of any income increase before lifestyle adjusts`,
              `Look for income-boosting moves: side income, freelance, promotions — the income side accelerates FI faster than cutting expenses`,
              `In the 3-5 years before FI, gradually de-risk: shift from growth ETFs toward dividend payers to protect gains`,
            ],
          };
          milestones.push({
            t: `~${pathFiYears} yr${pathFiYears !== 1 ? "s" : ""} (age ${pathFiAge})`,
            label: "Financial Independence",
            v: "$" + fiNumber.toLocaleString(),
            desc: risk === "aggressive"
              ? `Aggressive growth path puts FI potentially at age ${pathFiAge}. Stay the course through market downturns.`
              : risk === "conservative"
              ? `Steady path to FI at age ${pathFiAge}. Longer timeline, smoother ride, fewer sleepless nights.`
              : `At your current savings rate, work becomes optional at age ${pathFiAge}.`,
            steps: stepsM3[risk],
          });
        } else {
          const fiveYearTarget = Math.round((currentAssets + surplus*60) * 1.4);
          const displayTarget = Math.max(fiveYearTarget, 10000);
          const stepsM3 = {
            conservative: surplus <= 0 ? [
              `Close the income/expense gap first — audit every subscription and recurring charge`,
              `Even $100/month saved consistently in a HYSA builds the habit that leads to long-term wealth`,
              `Open a Roth IRA now even if you can only put in $25/month — the account needs to exist before you can grow it`,
            ] : [
              `Open a Roth IRA on Fidelity or Vanguard and invest in a target-date fund — set and forget`,
              `Capture any 401k employer match first — free money no investment can beat`,
              `Add CDs or I-Bonds for the bond portion — safe, predictable returns on idle cash`,
            ],
            balanced: surplus <= 0 ? [
              `Close the income/expense gap — audit every subscription and recurring charge this week`,
              `Even $200/month surplus changes everything over 5 years — find one expense to cut`,
              `Open a Roth IRA now even if you can only put in $25/month — starting the habit is the first win`,
            ] : [
              `Open a Roth IRA (Fidelity or Vanguard) and set up auto monthly contributions of $${Math.round(monthlyInvest).toLocaleString()}`,
              `Capture any 401k employer match first — guaranteed return that beats any fund`,
              `Invest in low-cost index funds (expense ratio under 0.10%) — fees are the silent killer of long-term returns`,
            ],
            aggressive: surplus <= 0 ? [
              `Close the income/expense gap — even $300/month invested in growth ETFs compounds fast`,
              `Look for income-boosting moves now: negotiate a raise, freelance, side hustle`,
              `Open a Roth IRA and taxable brokerage now — get the accounts set up so you're ready to deploy capital immediately`,
            ] : [
              `Open a taxable brokerage now and set up auto-DCA of $${Math.round(monthlyInvest).toLocaleString()}/month into QQQ or SCHG`,
              `Max your Roth IRA ($7,000/yr) — tax-free growth on your highest-conviction positions`,
              `Research 2-3 individual stocks or sector ETFs to add alongside your core index fund positions`,
            ],
          };
          milestones.push({
            t: "5 years",
            label: "5-Year Net Worth Goal",
            v: "$" + displayTarget.toLocaleString(),
            desc: surplus <= 0
              ? "Create a monthly surplus first — even $100/month consistently invested builds real wealth."
              : risk === "aggressive"
              ? `With growth positions, your 5-year potential is higher — but also more volatile than this estimate.`
              : `Realistic 5-year target at your current savings rate.`,
            steps: stepsM3[risk],
          });
        }
        return milestones;
      }

      return {
        allocation: alloc,
        fiYears:    pathFiYears,
        fiAge:      pathFiAge,
        fiNumber,
        actions:    getActions(),
        roadmap:    getRoadmap(),
        vehicles:   getVehicles(),
      };
    }

    await new Promise(r => setTimeout(r, 900));

    const defaultTab = form.riskTolerance === "conservative" ? "conservative"
                     : form.riskTolerance === "aggressive"   ? "aggressive"
                     : "balanced";

    const planObj = {
      snapshot: {
        netWorth, savingsRate, surplus, income, savings, investments, debt, age,
        strength: highRateDebt ? "solid income to tackle debt" : hasFund ? "emergency fund fully funded" : investments > 0 ? "already investing — great start" : "monthly surplus to work with",
        challenge: creditCardDebt ? "high-interest debt costing you monthly" : !hasFund ? "no emergency cushion yet" : savingsRate < 20 ? "savings rate has room to grow" : "consistency over time",
      },
      priority,
      stop,
      goals,
      catPriority,
      priorityTitle: priority.title,
      defaultTab,
      paths: {
        conservative: buildPath("conservative"),
        balanced:     buildPath("balanced"),
        aggressive:   buildPath("aggressive"),
      },
    };

    setPlan(planObj);
    savePlanResult(planObj);
    savePlanForm(form);
    setActiveTab(defaultTab);
    savePriority({ catPriority, priorityTitle:priority.title, generatedAt:today, risk:form.riskTolerance, surplus, savingsRate, debt, debtRate, investments, savings, expenses });

    (function seedTracker() {
      const existing = loadTD();
      const snap = { savings, invest:investments, emergency:0, creditCard:0, student:0, carLoan:0, other:0 };
      if (debt > 0) {
        if (debtRate > 10) snap.creditCard = debt;
        else if (debtRate > 4) snap.student = debt;
        else snap.other = debt;
      }
      const cats = JSON.parse(JSON.stringify(EMPTY_CATS));
      cats.emergency.target = emergTarget > 0 ? emergTarget : expenses*6;
      cats.savings.target   = savings > 0 ? savings*2 : 0;
      cats.invest.target    = investments > 0 ? investments*3 : 0;
      const entry = { date:today, snapshot:snap, note:"Imported from My Plan" };
      let next;
      if (!existing || !existing.history || existing.history.length === 0) {
        next = { categories:cats, history:[entry] };
      } else {
        const hist = existing.history.filter(h => h.date !== today);
        next = { ...existing, history:[...hist, entry].sort((a,b) => a.date.localeCompare(b.date)) };
      }
      persistTrackerRef.current(next);
    })();

    setLoading(false);
  }

  const inputStyle = {
    width:"100%", background:"#0f172a", border:"1.5px solid #334155", borderRadius:12,
    padding:"11px 14px", fontSize:15, fontWeight:700, color:"#f1f5f9",
    outline:"none", boxSizing:"border-box" as const, fontFamily:"Nunito",
  };
  const labelStyle = { fontSize:12, fontWeight:800, color:"#94a3b8", textTransform:"uppercase" as const, letterSpacing:1, marginBottom:5, display:"block" };
  const rowStyle   = { marginBottom:16 };

  // ── PLAN RESULT VIEW ──
  if (plan) {
    const s   = plan.snapshot;
    const fmt = n => n >= 0 ? `$${Math.abs(Math.round(n)).toLocaleString()}` : `-$${Math.abs(Math.round(n)).toLocaleString()}`;
    const tab  = activeTab as keyof typeof PATH_META;
    const meta = PATH_META[tab];
    const path = plan.paths[tab];
    const alloc = path.allocation;

    // Build allocation display segments (skip zeros)
    const allocSegments = [
      { l:"Stocks",  v:alloc.stocks,       c:"#f59e0b", desc:"Growth engine",   bar:true },
      alloc.bonds > 0        && { l:"Bonds",  v:alloc.bonds,       c:"#8b5cf6", desc:"Stability",      bar:true },
      alloc.reit > 0         && { l:"REITs",  v:alloc.reit,        c:"#10b981", desc:"Real estate",    bar:true },
      alloc.alternatives > 0 && { l:"Alt",    v:alloc.alternatives,c:"#ef4444", desc:"Crypto/stocks",  bar:true },
    ].filter(Boolean) as { l:string; v:number; c:string; desc:string; bar:boolean }[];

    return (
      <div style={{ fontFamily:"Nunito", background:"#0f172a", minHeight:"100vh", paddingBottom:120 }}>
        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,#1e3a5f,#0f172a)", padding:"18px 16px 14px", borderBottom:"1px solid #1e293b" }}>
          <div style={{ fontSize:11, color:"#64748b", fontWeight:800, textTransform:"uppercase", letterSpacing:2, marginBottom:4 }}>Your Personalized Plan</div>
          <div style={{ fontSize:22, fontWeight:900, color:"#f1f5f9", fontFamily:"Fredoka One", lineHeight:1.1, marginBottom:4 }}>🎯 Your Financial Roadmap</div>
          <div style={{ fontSize:13, color:"#64748b", fontWeight:700 }}>Choose your path · Scroll to explore</div>
        </div>

        {/* Path tabs */}
        <div style={{ display:"flex", gap:6, padding:"12px 16px 0" }}>
          {(["conservative","balanced","aggressive"] as (keyof typeof PATH_META)[]).map(t => {
            const m   = PATH_META[t];
            const act = t === activeTab;
            return (
              <button key={t} onClick={() => setActiveTab(t)}
                style={{ flex:1, background: act ? `${m.color}22` : "#1e293b",
                  border:`2px solid ${act ? m.color : "#334155"}`,
                  borderRadius:12, padding:"9px 4px", cursor:"pointer",
                  textAlign:"center", transition:"all 0.2s" }}>
                <div style={{ fontSize:18, marginBottom:3 }}>{m.emoji}</div>
                <div style={{ fontSize:10, fontWeight:900, color: act ? m.color : "#475569",
                  textTransform:"uppercase", letterSpacing:0.8, lineHeight:1.2 }}>{m.label}</div>
              </button>
            );
          })}
        </div>

        {/* Path description badge */}
        <div style={{ margin:"10px 16px 0", background:`${meta.color}14`,
          border:`1px solid ${meta.color}44`, borderRadius:14, padding:"10px 14px",
          display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ fontSize:24 }}>{meta.emoji}</div>
          <div>
            <div style={{ fontSize:13, fontWeight:900, color:meta.color }}>{meta.label} Path</div>
            <div style={{ fontSize:12, color:"#64748b", fontWeight:700 }}>{meta.desc}</div>
          </div>
          {path.fiYears > 0 && path.fiYears <= 50 && (
            <div style={{ marginLeft:"auto", textAlign:"right", flexShrink:0 }}>
              <div style={{ fontSize:14, fontWeight:900, color:meta.color }}>Age {path.fiAge}</div>
              <div style={{ fontSize:10, color:"#475569", fontWeight:700 }}>FI projection</div>
            </div>
          )}
        </div>

        <div style={{ padding:"12px 16px", display:"flex", flexDirection:"column", gap:12 }}>

          {/* Snapshot */}
          <div style={{ background:"#1e293b", borderRadius:18, padding:"16px", border:"1px solid #334155" }}>
            <div style={{ fontSize:11, fontWeight:900, color:"#0ea5e9", textTransform:"uppercase", letterSpacing:1.5, marginBottom:12 }}>📊 Your Financial Snapshot</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12 }}>
              {[
                { l:"Net Worth",      v:fmt(s.netWorth),    c: s.netWorth >= 0 ? "#10b981" : "#ef4444" },
                { l:"Savings Rate",   v:`${s.savingsRate}%`, c: s.savingsRate >= 20 ? "#10b981" : s.savingsRate >= 10 ? "#f59e0b" : "#ef4444" },
                { l:"Monthly Surplus",v:fmt(s.surplus),     c: s.surplus >= 0 ? "#10b981" : "#ef4444" },
                { l:"Invested Assets",v:fmt(s.investments), c:"#0ea5e9" },
              ].map(p => (
                <div key={p.l} style={{ background:"#0f172a", borderRadius:12, padding:"12px", textAlign:"center" }}>
                  <div style={{ fontSize:18, fontWeight:900, color:p.c }}>{p.v}</div>
                  <div style={{ fontSize:10, color:"#475569", fontWeight:700, textTransform:"uppercase", letterSpacing:0.8, marginTop:2 }}>{p.l}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              <div style={{ background:"#0f172a", borderRadius:12, padding:"10px 12px" }}>
                <div style={{ fontSize:10, color:"#10b981", fontWeight:900, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>💪 Key Strength</div>
                <div style={{ fontSize:12, color:"#cbd5e1", fontWeight:700, lineHeight:1.4, textTransform:"capitalize" }}>{s.strength}</div>
              </div>
              <div style={{ background:"#0f172a", borderRadius:12, padding:"10px 12px" }}>
                <div style={{ fontSize:10, color:"#f59e0b", fontWeight:900, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>⚡ Key Focus</div>
                <div style={{ fontSize:12, color:"#cbd5e1", fontWeight:700, lineHeight:1.4, textTransform:"capitalize" }}>{s.challenge}</div>
              </div>
            </div>
          </div>

          {/* Priority */}
          <div style={{ background:"linear-gradient(135deg,#0ea5e922,#0ea5e908)", border:"2px solid #0ea5e955", borderRadius:18, padding:"16px" }}>
            <div style={{ fontSize:11, fontWeight:900, color:"#0ea5e9", textTransform:"uppercase", letterSpacing:1.5, marginBottom:8 }}>🔥 Your #1 Priority Right Now</div>
            <div style={{ fontSize:15, fontWeight:900, color:"#f1f5f9", marginBottom:10, lineHeight:1.3 }}>{plan.priority.title}</div>
            <div style={{ fontSize:13, color:"#94a3b8", fontWeight:700, lineHeight:1.7 }}>{plan.priority.body}</div>
          </div>

          {/* Investment strategy — PATH SPECIFIC */}
          <div style={{ background:"#1e293b", borderRadius:18, padding:"16px", border:`1px solid ${meta.color}44` }}>
            <div style={{ fontSize:11, fontWeight:900, color:meta.color, textTransform:"uppercase", letterSpacing:1.5, marginBottom:8 }}>📈 {meta.emoji} {meta.label} Investment Strategy</div>
            <div style={{ fontSize:13, color:"#64748b", fontWeight:700, marginBottom:12 }}>
              Based on age {form.age} · {meta.label} approach · ~{(GROWTH_RATES[tab]*12*100).toFixed(1)}%/yr projected growth
            </div>
            {/* Allocation bar */}
            <div style={{ display:"flex", borderRadius:10, overflow:"hidden", height:14, marginBottom:10 }}>
              {allocSegments.map(a => (
                <div key={a.l} style={{ width:`${a.v}%`, background:a.c }} />
              ))}
            </div>
            <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
              {allocSegments.map(a => (
                <div key={a.l} style={{ flex:"1 1 0", minWidth:60, background:"#0f172a", borderRadius:10, padding:"10px 8px", textAlign:"center", border:`2px solid ${a.c}33` }}>
                  <div style={{ fontSize:18, fontWeight:900, color:a.c }}>{a.v}%</div>
                  <div style={{ fontSize:11, fontWeight:800, color:"#f1f5f9", marginTop:2 }}>{a.l}</div>
                  <div style={{ fontSize:9, color:"#475569", fontWeight:700 }}>{a.desc}</div>
                </div>
              ))}
            </div>
            {/* Vehicle steps */}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {path.vehicles.map((v, i) => (
                <div key={i} style={{ display:"flex", gap:10, background:"#0f172a", borderRadius:10, padding:"10px 12px", alignItems:"flex-start" }}>
                  <div style={{ fontSize:10, fontWeight:900, color:meta.color, flexShrink:0, marginTop:2, textTransform:"uppercase", letterSpacing:0.8 }}>{v.a}</div>
                  <div style={{ fontSize:12, color:"#94a3b8", fontWeight:700, lineHeight:1.5 }}>{v.d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 30-day actions — PATH SPECIFIC */}
          <div style={{ background:"#1e293b", borderRadius:18, padding:"16px", border:"1px solid #334155" }}>
            <div style={{ fontSize:11, fontWeight:900, color:"#10b981", textTransform:"uppercase", letterSpacing:1.5, marginBottom:12 }}>✅ Your 30-Day Action Plan</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {path.actions.map((a, i) => (
                <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
                  <div style={{ width:26, height:26, borderRadius:"50%", background:"#10b981", flexShrink:0,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:12, fontWeight:900, color:"#fff", marginTop:1 }}>{i+1}</div>
                  <div style={{ fontSize:13, color:"#cbd5e1", fontWeight:700, lineHeight:1.6, flex:1 }}>{a}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Roadmap — PATH SPECIFIC */}
          <RoadmapSection roadmap={path.roadmap} accentColor={meta.color} />

          {/* Stop doing */}
          <div style={{ background:"linear-gradient(135deg,#7f1d1d33,#7f1d1d11)", border:"2px solid #ef444455", borderRadius:18, padding:"16px" }}>
            <div style={{ fontSize:11, fontWeight:900, color:"#ef4444", textTransform:"uppercase", letterSpacing:1.5, marginBottom:8 }}>🚫 One Thing to Stop Doing</div>
            <div style={{ fontSize:14, fontWeight:900, color:"#f1f5f9", marginBottom:8, lineHeight:1.3 }}>{plan.stop.t}</div>
            <div style={{ fontSize:13, color:"#94a3b8", fontWeight:700, lineHeight:1.7 }}>{plan.stop.d}</div>
          </div>

          {/* FI teaser — PATH SPECIFIC */}
          {path.fiYears > 0 && path.fiYears <= 50 ? (
            <div style={{ background:`linear-gradient(135deg,${meta.color}22,${meta.color}08)`, border:`2px solid ${meta.color}55`, borderRadius:18, padding:"16px", textAlign:"center" }}>
              <div style={{ fontSize:28, marginBottom:6 }}>🏁</div>
              <div style={{ fontSize:13, color:meta.color, fontWeight:900, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>
                {meta.emoji} {meta.label} Path — FI Projection
              </div>
              <div style={{ fontSize:22, fontWeight:900, color:"#f1f5f9", fontFamily:"Fredoka One" }}>
                Age {path.fiAge} · {path.fiYears} year{path.fiYears !== 1 ? "s" : ""} away
              </div>
              <div style={{ fontSize:13, color:"#64748b", fontWeight:700, marginTop:6, lineHeight:1.5 }}>
                At ~{(GROWTH_RATES[tab]*12*100).toFixed(1)}%/yr growth. Every extra $100/month saved shaves roughly a year off that timeline.
              </div>
            </div>
          ) : (
            <div style={{ background:"linear-gradient(135deg,#1e293b,#0f172a)", border:"1px solid #334155", borderRadius:18, padding:"16px", textAlign:"center" }}>
              <div style={{ fontSize:24, marginBottom:6 }}>🌱</div>
              <div style={{ fontSize:13, color:"#0ea5e9", fontWeight:900, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Building Your Foundation</div>
              <div style={{ fontSize:13, color:"#64748b", fontWeight:700, lineHeight:1.6 }}>
                {plan.snapshot.surplus <= 0
                  ? "Right now expenses exceed income — focus on closing that gap first. Even a $200/month surplus invested consistently builds real wealth."
                  : "The fastest lever: increase your monthly surplus. Going from saving 5% to 20% of income can cut the FI timeline in half."}
              </div>
              <div style={{ marginTop:12, background:"#0ea5e922", border:"1px solid #0ea5e944", borderRadius:12, padding:"10px 14px" }}>
                <div style={{ fontSize:12, color:"#0ea5e9", fontWeight:900 }}>
                  FI target: ${path.fiNumber?.toLocaleString()} · Saving ${Math.round(plan.snapshot.surplus||0).toLocaleString()}/mo
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <button onClick={() => { setPlan(null); setStep(0); }} className="btn-bounce"
            style={{ width:"100%", background:"#1e293b", border:"2px solid #334155",
              borderRadius:16, padding:"14px", fontSize:14, fontWeight:800,
              color:"#64748b", cursor:"pointer" }}>
            ← Update My Info
          </button>
          <button onClick={generatePlan} className="btn-bounce"
            style={{ width:"100%", background:"linear-gradient(135deg,#0ea5e9,#0c4a6e)",
              border:"none", borderRadius:16, padding:"14px", fontSize:14, fontWeight:900,
              color:"#fff", cursor:"pointer", boxShadow:"0 6px 20px #0ea5e944" }}>
            🔄 Recalculate Plan
          </button>
          <button onClick={handleReset}
            style={{ background:"none", border:"none", color:"#475569", fontSize:12,
              fontWeight:700, cursor:"pointer", padding:"8px", width:"100%", textAlign:"center" }}>
            🗑️ Reset all Pro data
          </button>
        </div>
      </div>
    );
  }

  // ── LOADING ──
  if (loading) {
    return (
      <div style={{ fontFamily:"Nunito", background:"#0f172a", minHeight:"100vh",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 24px", gap:20 }}>
        <div style={{ fontSize:52, animation:"pulse 1.2s infinite" }}>🎯</div>
        <div style={{ fontSize:20, fontWeight:900, color:"#f1f5f9", fontFamily:"Fredoka One", textAlign:"center" }}>
          Building Your 3 Paths...
        </div>
        <div style={{ fontSize:14, color:"#64748b", fontWeight:700, textAlign:"center", lineHeight:1.6 }}>
          Crafting your Conservative, Balanced, and Aggressive plans
        </div>
        <div style={{ display:"flex", gap:8, marginTop:8 }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width:10, height:10, borderRadius:"50%", background:"#0ea5e9",
              animation:`pulse ${0.8+i*0.2}s infinite`, opacity:0.7+i*0.15 }}/>
          ))}
        </div>
      </div>
    );
  }

  // ── INTAKE FORM ──
  return (
    <div style={{ fontFamily:"Nunito", background:"#0f172a", minHeight:"100vh", paddingBottom:120 }}>
      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#1e3a5f,#0f172a)", padding:"18px 16px 20px", borderBottom:"1px solid #1e293b" }}>
        <div style={{ fontSize:11, color:"#64748b", fontWeight:800, textTransform:"uppercase", letterSpacing:2, marginBottom:4 }}>Personalized Planning</div>
        <div style={{ fontSize:22, fontWeight:900, color:"#f1f5f9", fontFamily:"Fredoka One", lineHeight:1.1, marginBottom:4 }}>🎯 My Financial Plan</div>
        <div style={{ fontSize:13, color:"#64748b", fontWeight:700 }}>Answer a few questions · Get 3 personalized paths</div>
      </div>

      {/* Step indicators */}
      <div style={{ padding:"14px 16px 0" }}>
        <div style={{ display:"flex", gap:4, marginBottom:8 }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{ flex:1, height:5, borderRadius:3,
              background: i <= step ? "#0ea5e9" : "#1e293b", transition:"background 0.3s" }}/>
          ))}
        </div>
        <div style={{ display:"flex", gap:4, marginBottom:4 }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{ flex:1, textAlign:"center", fontSize:9, fontWeight:800,
              color: i === step ? "#0ea5e9" : i < step ? "#10b981" : "#334155",
              textTransform:"uppercase", letterSpacing:0.8, transition:"color 0.3s" }}>
              {i < step ? "✓ " : ""}{s.emoji} {s.label}
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding:"16px" }}>

        {/* ── STEP 0: BASICS ── */}
        {step === 0 && (
          <div style={{ animation:"slideUp 0.3s" }}>
            <div style={{ background:"#1e293b", borderRadius:18, padding:"18px", marginBottom:16, border:"1px solid #334155" }}>
              <div style={{ fontSize:16, fontWeight:900, color:"#f1f5f9", marginBottom:4 }}>👤 The Basics</div>
              <div style={{ fontSize:13, color:"#64748b", fontWeight:700, marginBottom:18 }}>A few fundamentals about your situation</div>

              <div style={rowStyle}>
                <label style={labelStyle}>Your Age</label>
                <input style={inputStyle} type="number" placeholder="e.g. 26" value={form.age}
                  onChange={e => set("age", e.target.value)}/>
              </div>
              <div style={rowStyle}>
                <label style={labelStyle}>Annual Income (before taxes) $</label>
                <input style={inputStyle} type="number" placeholder="e.g. 72000" value={form.income}
                  onChange={e => set("income", e.target.value)}/>
                <div style={{ fontSize:11, color:"#475569", fontWeight:700, marginTop:5 }}>Include salary, freelance, side income — everything</div>
              </div>
              <div style={rowStyle}>
                <label style={labelStyle}>Monthly Expenses (all in) $</label>
                <input style={inputStyle} type="number" placeholder="e.g. 3200" value={form.monthlyExpenses}
                  onChange={e => set("monthlyExpenses", e.target.value)}/>
                <div style={{ fontSize:11, color:"#475569", fontWeight:700, marginTop:5 }}>Rent, food, subscriptions, going out — everything you spend</div>
              </div>

              {form.income && form.monthlyExpenses && (() => {
                const mo = (parseFloat(form.income)||0)/12;
                const ex = parseFloat(form.monthlyExpenses)||0;
                const sur = mo - ex;
                const sr = mo > 0 ? Math.round((sur/mo)*100) : 0;
                return (
                  <div style={{ background:"#0f172a", borderRadius:12, padding:"12px 14px", border:"1px solid #0ea5e933", marginTop:4 }}>
                    <div style={{ fontSize:12, fontWeight:800, color:"#0ea5e9", marginBottom:4 }}>💡 Your Monthly Snapshot</div>
                    <div style={{ display:"flex", gap:10 }}>
                      {[
                        { v:`$${sur.toFixed(0)}`, l:"monthly surplus", c: sur >= 0 ? "#10b981" : "#ef4444" },
                        { v:`${sr}%`,              l:"savings rate",    c: sr >= 20 ? "#10b981" : sr >= 10 ? "#f59e0b" : "#ef4444" },
                        { v:`$${mo.toFixed(0)}`,   l:"monthly income",  c:"#f1f5f9" },
                      ].map(p => (
                        <div key={p.l} style={{ flex:1, textAlign:"center" }}>
                          <div style={{ fontSize:16, fontWeight:900, color:p.c }}>{p.v}</div>
                          <div style={{ fontSize:10, color:"#475569", fontWeight:700 }}>{p.l}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── STEP 1: MONEY ── */}
        {step === 1 && (
          <div style={{ animation:"slideUp 0.3s" }}>
            <div style={{ background:"#1e293b", borderRadius:18, padding:"18px", marginBottom:16, border:"1px solid #334155" }}>
              <div style={{ fontSize:16, fontWeight:900, color:"#f1f5f9", marginBottom:4 }}>💰 Your Money</div>
              <div style={{ fontSize:13, color:"#64748b", fontWeight:700, marginBottom:18 }}>What you have saved and invested right now</div>

              <div style={rowStyle}>
                <label style={labelStyle}>Cash Savings (checking + savings) $</label>
                <input style={inputStyle} type="number" placeholder="e.g. 8000" value={form.savings}
                  onChange={e => set("savings", e.target.value)}/>
                <div style={{ fontSize:11, color:"#475569", fontWeight:700, marginTop:5 }}>Bank accounts, money market, cash on hand</div>
              </div>
              <div style={rowStyle}>
                <label style={labelStyle}>Invested Assets $</label>
                <input style={inputStyle} type="number" placeholder="e.g. 15000" value={form.investments}
                  onChange={e => set("investments", e.target.value)}/>
                <div style={{ fontSize:11, color:"#475569", fontWeight:700, marginTop:5 }}>401k, Roth IRA, brokerage, crypto — anything invested</div>
              </div>
              <div style={rowStyle}>
                <label style={labelStyle}>Risk Comfort Level</label>
                <div style={{ display:"flex", gap:8 }}>
                  {[
                    { v:"conservative", l:"🛡️ Conservative", d:"Protect what I have" },
                    { v:"medium",       l:"⚖️ Balanced",     d:"Some risk for growth" },
                    { v:"aggressive",   l:"🚀 Aggressive",   d:"Max growth long-term" },
                  ].map(r => (
                    <button key={r.v} onClick={() => set("riskTolerance", r.v)}
                      style={{ flex:1, background: form.riskTolerance === r.v ? "#0ea5e922" : "#0f172a",
                        border:`2px solid ${form.riskTolerance === r.v ? "#0ea5e9" : "#334155"}`,
                        borderRadius:12, padding:"10px 6px", cursor:"pointer", textAlign:"center", transition:"all 0.2s" }}>
                      <div style={{ fontSize:14, marginBottom:3 }}>{r.l.split(" ")[0]}</div>
                      <div style={{ fontSize:10, fontWeight:800, color: form.riskTolerance === r.v ? "#0ea5e9" : "#475569" }}>{r.l.split(" ").slice(1).join(" ")}</div>
                      <div style={{ fontSize:9, color:"#334155", marginTop:2, fontWeight:700 }}>{r.d}</div>
                    </button>
                  ))}
                </div>
                <div style={{ fontSize:11, color:"#475569", fontWeight:700, marginTop:8, lineHeight:1.5 }}>
                  Don't worry — you'll see all 3 plans side-by-side and can switch between them.
                </div>
              </div>

              {(form.savings || form.investments) && (() => {
                const sv = parseFloat(form.savings)||0;
                const inv = parseFloat(form.investments)||0;
                const et = (parseFloat(form.monthlyExpenses)||0) * 6;
                const hf = sv >= et && et > 0;
                return (
                  <div style={{ background:"#0f172a", borderRadius:12, padding:"12px 14px", border:"1px solid #0ea5e933" }}>
                    <div style={{ fontSize:12, fontWeight:800, color:"#0ea5e9", marginBottom:4 }}>💰 Assets So Far</div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                      <span style={{ fontSize:13, color:"#64748b", fontWeight:700 }}>Total assets</span>
                      <span style={{ fontSize:13, fontWeight:900, color:"#10b981" }}>${(sv+inv).toLocaleString()}</span>
                    </div>
                    {et > 0 && (
                      <div style={{ display:"flex", justifyContent:"space-between" }}>
                        <span style={{ fontSize:13, color:"#64748b", fontWeight:700 }}>Emergency fund</span>
                        <span style={{ fontSize:13, fontWeight:900, color: hf ? "#10b981" : "#f59e0b" }}>
                          {hf ? "✅ Funded" : "⚠️ Need $"+(et-sv).toFixed(0)+" more"}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── STEP 2: DEBT ── */}
        {step === 2 && (
          <div style={{ animation:"slideUp 0.3s" }}>
            <div style={{ background:"#1e293b", borderRadius:18, padding:"18px", marginBottom:16, border:"1px solid #334155" }}>
              <div style={{ fontSize:16, fontWeight:900, color:"#f1f5f9", marginBottom:4 }}>💳 Your Debt</div>
              <div style={{ fontSize:13, color:"#64748b", fontWeight:700, marginBottom:18 }}>Total debt you're carrying — skip if none</div>

              <div style={rowStyle}>
                <label style={labelStyle}>Total Debt Amount $</label>
                <input style={inputStyle} type="number" placeholder="e.g. 24000" value={form.debt}
                  onChange={e => set("debt", e.target.value)}/>
                <div style={{ fontSize:11, color:"#475569", fontWeight:700, marginTop:5 }}>All debts combined: student loans, car, credit cards, etc.</div>
              </div>
              <div style={rowStyle}>
                <label style={labelStyle}>Average Interest Rate on Your Debt %</label>
                <input style={inputStyle} type="number" placeholder="e.g. 7" value={form.debtRate}
                  onChange={e => set("debtRate", e.target.value)}/>
                <div style={{ fontSize:11, color:"#475569", fontWeight:700, marginTop:5 }}>If mixed, use a rough average. Credit cards: ~22%, student loans: ~5-7%</div>
              </div>

              {form.debt && form.debtRate && (() => {
                const d = parseFloat(form.debt)||0;
                const r = parseFloat(form.debtRate)||0;
                const monthly = d*r/100/12;
                const isHigh = r > 10;
                return (
                  <div style={{ background:"#0f172a", borderRadius:12, padding:"12px 14px", border:"1px solid #ef444433" }}>
                    <div style={{ fontSize:12, fontWeight:800, color:"#ef4444", marginBottom:4 }}>📊 Debt Reality Check</div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                      <span style={{ fontSize:13, color:"#64748b", fontWeight:700 }}>Monthly interest cost</span>
                      <span style={{ fontSize:13, fontWeight:900, color:"#ef4444" }}>${monthly.toFixed(0)}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:13, color:"#64748b", fontWeight:700 }}>Annual interest cost</span>
                      <span style={{ fontSize:13, fontWeight:900, color:"#ef4444" }}>${(monthly*12).toFixed(0)}</span>
                    </div>
                    {isHigh && <div style={{ fontSize:11, color:"#f59e0b", fontWeight:800, marginTop:8 }}>⚠️ High rate — paying this off beats most investments</div>}
                  </div>
                );
              })()}

              <div style={{ background:"#0f172a", borderRadius:12, padding:"12px 14px", border:"1px solid #334155", marginTop:12 }}>
                <div style={{ fontSize:12, fontWeight:800, color:"#64748b", marginBottom:6 }}>💡 No debt? Great!</div>
                <div style={{ fontSize:12, color:"#475569", fontWeight:700, lineHeight:1.5 }}>Leave both fields blank and we'll focus your plan entirely on building wealth.</div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: GOALS ── */}
        {step === 3 && (
          <div style={{ animation:"slideUp 0.3s" }}>
            <div style={{ background:"#1e293b", borderRadius:18, padding:"18px", marginBottom:16, border:"1px solid #334155" }}>
              <div style={{ fontSize:16, fontWeight:900, color:"#f1f5f9", marginBottom:4 }}>🎯 Your Goals</div>
              <div style={{ fontSize:13, color:"#64748b", fontWeight:700, marginBottom:16 }}>What are you actually working toward?</div>

              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>Select all that apply</label>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {GOAL_OPTIONS.map(g => {
                    const sel = form.goals.includes(g);
                    return (
                      <button key={g} onClick={() => toggleGoal(g)} className="btn-bounce"
                        style={{ background: sel ? "#0ea5e922" : "#0f172a",
                          border:`2px solid ${sel ? "#0ea5e9" : "#334155"}`,
                          borderRadius:20, padding:"7px 14px", cursor:"pointer",
                          fontSize:12, fontWeight:800, color: sel ? "#0ea5e9" : "#475569",
                          transition:"all 0.15s" }}>
                        {g}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={rowStyle}>
                <label style={labelStyle}>Something else? (optional)</label>
                <input style={inputStyle} type="text" placeholder="e.g. Start a food truck business"
                  value={form.customGoal} onChange={e => set("customGoal", e.target.value)}/>
              </div>
              <div style={rowStyle}>
                <label style={labelStyle}>Timeline to your most important goal</label>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {["1-2 years","3-5 years","5-10 years","10+ years"].map(t => (
                    <button key={t} onClick={() => set("timeline", t)} className="btn-bounce"
                      style={{ flex:1, minWidth:"calc(50% - 3px)",
                        background: form.timeline === t ? "#0ea5e922" : "#0f172a",
                        border:`2px solid ${form.timeline === t ? "#0ea5e9" : "#334155"}`,
                        borderRadius:12, padding:"10px 8px", cursor:"pointer",
                        fontSize:12, fontWeight:800, color: form.timeline === t ? "#0ea5e9" : "#475569",
                        textAlign:"center", transition:"all 0.15s" }}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary before generating */}
            <div style={{ background:"#1e293b", borderRadius:16, padding:"14px", marginBottom:16, border:"1px solid #0ea5e933" }}>
              <div style={{ fontSize:12, fontWeight:900, color:"#0ea5e9", marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>📋 Your Profile Summary</div>
              {[
                { l:"Age",           v: form.age || "—" },
                { l:"Annual income", v: form.income ? "$"+parseInt(form.income).toLocaleString() : "—" },
                { l:"Monthly surplus",v: form.income && form.monthlyExpenses ? "$"+(((parseFloat(form.income)||0)/12-(parseFloat(form.monthlyExpenses)||0)).toFixed(0))+"/mo" : "—" },
                { l:"Cash savings",  v: form.savings ? "$"+parseInt(form.savings).toLocaleString() : "—" },
                { l:"Invested assets",v: form.investments ? "$"+parseInt(form.investments).toLocaleString() : "—" },
                { l:"Total debt",    v: form.debt ? "$"+parseInt(form.debt).toLocaleString()+" at "+(form.debtRate||"?")+"% interest" : "None" },
                { l:"Risk preference",v: { conservative:"🛡️ Conservative", medium:"⚖️ Balanced", aggressive:"🚀 Aggressive" }[form.riskTolerance] },
                { l:"Goals",         v: (form.goals.length > 0 ? form.goals.slice(0,2).join(", ")+(form.goals.length>2?" +more":"") : form.customGoal) || "—" },
              ].map(r => (
                <div key={r.l} style={{ display:"flex", justifyContent:"space-between",
                  padding:"5px 0", borderBottom:"1px solid #0f172a" }}>
                  <span style={{ fontSize:12, color:"#475569", fontWeight:700 }}>{r.l}</span>
                  <span style={{ fontSize:12, color:"#f1f5f9", fontWeight:800, textAlign:"right", maxWidth:"55%" }}>{r.v}</span>
                </div>
              ))}
              <div style={{ marginTop:10, background:"#0ea5e911", borderRadius:10, padding:"8px 12px", border:"1px solid #0ea5e933" }}>
                <div style={{ fontSize:11, color:"#0ea5e9", fontWeight:800 }}>
                  🛡️ Conservative · ⚖️ Balanced · 🚀 Aggressive plans will all be generated — compare and choose your path.
                </div>
              </div>
            </div>

            {error && (
              <div style={{ background:"#7f1d1d44", border:"2px solid #ef4444", borderRadius:14,
                padding:"12px 14px", marginBottom:14, fontSize:13, fontWeight:700, color:"#fca5a5" }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display:"flex", gap:10 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s-1)} className="btn-bounce"
              style={{ flex:1, background:"#1e293b", border:"2px solid #334155",
                borderRadius:16, padding:"15px", fontSize:15, fontWeight:800,
                color:"#64748b", cursor:"pointer" }}>
              ← Back
            </button>
          )}
          {step < STEPS.length-1 ? (
            <button onClick={() => setStep(s => s+1)} disabled={!canAdvance()} className="btn-bounce"
              style={{ flex:1, background: canAdvance() ? "linear-gradient(135deg,#0ea5e9,#0c4a6e)" : "#1e293b",
                border:`2px solid ${canAdvance() ? "#0ea5e9" : "#334155"}`,
                borderRadius:16, padding:"15px", fontSize:15, fontWeight:900,
                color: canAdvance() ? "#fff" : "#334155",
                cursor: canAdvance() ? "pointer" : "not-allowed", transition:"all 0.2s" }}>
              Next →
            </button>
          ) : (
            <button onClick={generatePlan} disabled={!canAdvance() || loading} className="btn-bounce"
              style={{ flex:1, background: canAdvance() ? "linear-gradient(135deg,#0ea5e9,#0c4a6e)" : "#1e293b",
                border:`2px solid ${canAdvance() ? "#0ea5e9" : "#334155"}`,
                borderRadius:16, padding:"15px", fontSize:15, fontWeight:900,
                color: canAdvance() ? "#fff" : "#334155",
                cursor: canAdvance() ? "pointer" : "not-allowed",
                boxShadow: canAdvance() ? "0 6px 20px #0ea5e944" : "none", transition:"all 0.2s" }}>
              🎯 Build My 3 Plans
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
